/**
 * TJC Programme Guide — fetch today's schedule via Worker proxy and render table.
 */

const PROGRAMME_GUIDE_TAG = 'programme-guide';

class ProgrammeGuide extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    const retryBtn = this.querySelector('[data-programme-guide-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', this.#handleRetry);
    }

    this.#load();
  }

  disconnectedCallback() {
    const retryBtn = this.querySelector('[data-programme-guide-retry]');
    if (retryBtn) {
      retryBtn.removeEventListener('click', this.#handleRetry);
    }

    this.#abortController?.abort();
  }

  #handleRetry = () => {
    this.#load();
  };

  async #load() {
    const apiUrl = (this.dataset.apiUrl || '').trim();
    const statusEl = this.querySelector('[data-programme-guide-status]');
    const errorEl = this.querySelector('[data-programme-guide-error]');
    const errorMessageEl = this.querySelector('[data-programme-guide-error-message]');
    const tableWrap = this.querySelector('[data-programme-guide-table-wrap]');
    const tbody = this.querySelector('[data-programme-guide-body]');

    if (!apiUrl || !tbody) {
      this.#showError(this.dataset.errorLabel || 'Unable to load programme guide.');
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.setAttribute('aria-busy', 'true');
    if (statusEl) {
      statusEl.hidden = false;
      statusEl.textContent = this.dataset.loadingLabel || 'Loading programme guide…';
    }
    if (errorEl) errorEl.hidden = true;
    if (tableWrap) tableWrap.hidden = true;
    tbody.replaceChildren();

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const payload = await response.json();
      const items = Array.isArray(payload?.items) ? payload.items : [];

      if (!items.length) {
        if (statusEl) {
          statusEl.hidden = false;
          statusEl.textContent = this.dataset.emptyLabel || 'No programmes scheduled for today.';
        }
        this.removeAttribute('aria-busy');
        return;
      }

      const fragment = document.createDocumentFragment();

      for (const item of items) {
        const tr = document.createElement('tr');

        const timeTd = document.createElement('td');
        timeTd.className = 'programme-guide__cell programme-guide__cell--time';
        timeTd.textContent = item?.time || '—';

        const titleTd = document.createElement('td');
        titleTd.className = 'programme-guide__cell programme-guide__cell--title';
        titleTd.textContent = item?.title || '—';

        const synopsisTd = document.createElement('td');
        synopsisTd.className = 'programme-guide__cell programme-guide__cell--synopsis';
        synopsisTd.textContent = item?.synopsis || '—';

        tr.append(timeTd, titleTd, synopsisTd);
        fragment.append(tr);
      }

      tbody.append(fragment);

      if (statusEl) statusEl.hidden = true;
      if (tableWrap) tableWrap.hidden = false;
      this.removeAttribute('aria-busy');
    } catch (error) {
      if (error?.name === 'AbortError') return;

      this.#showError(
        this.dataset.errorLabel || 'Unable to load programme guide. Please try again.'
      );
      this.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {string} message
   */
  #showError(message) {
    const statusEl = this.querySelector('[data-programme-guide-status]');
    const errorEl = this.querySelector('[data-programme-guide-error]');
    const errorMessageEl = this.querySelector('[data-programme-guide-error-message]');
    const tableWrap = this.querySelector('[data-programme-guide-table-wrap]');

    if (statusEl) statusEl.hidden = true;
    if (tableWrap) tableWrap.hidden = true;
    if (errorMessageEl) errorMessageEl.textContent = message;
    if (errorEl) errorEl.hidden = false;
  }
}

if (!customElements.get(PROGRAMME_GUIDE_TAG)) {
  customElements.define(PROGRAMME_GUIDE_TAG, ProgrammeGuide);
}
